import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    FiAlertTriangle,
    FiArrowLeft,
    FiCheckCircle,
    FiCreditCard,
    FiFileText,
    FiLoader,
    FiPrinter,
    FiRefreshCw,
    FiRotateCcw,
    FiUser,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    useHistory,
    useParams,
} from "react-router-dom";

import {
    clearSelectedPaymentOut,
    fetchPaymentOutById,
    reversePaymentOut,
    selectedPaymentOutSelector,
    paymentOutLoadingSelector,
    paymentOutErrorSelector,
} from "../../../../../../store/billing/paymentOut/paymentOutSlice";

import ReversePaymentOutDialog from "./components/ReversePaymentOutDialog";

import "./PaymentOut.css";


/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(
        number
    )
        ? number
        : fallback;
};


const roundMoney = (
    value
) =>
    Math.round(
        (
            toNumber(value) +
            Number.EPSILON
        ) *
            100
    ) / 100;


const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style:
                "currency",

            currency:
                "INR",

            minimumFractionDigits:
                2,

            maximumFractionDigits:
                2,
        }
    ).format(
        roundMoney(value)
    );


const formatDate = (
    value,
    includeTime = false
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        includeTime
            ? {
                  day:
                      "2-digit",

                  month:
                      "short",

                  year:
                      "numeric",

                  hour:
                      "2-digit",

                  minute:
                      "2-digit",
              }
            : {
                  day:
                      "2-digit",

                  month:
                      "short",

                  year:
                      "numeric",
              }
    ).format(
        date
    );
};


const formatLabel = (
    value
) =>
    String(
        value ||
            "-"
    )
        .replace(
            /_/g,
            " "
        )
        .toLowerCase()
        .replace(
            /\b\w/g,
            (
                character
            ) =>
                character.toUpperCase()
        );


const getSupplierName = (
    payment
) =>
    payment
        ?.supplierSnapshot
        ?.supplierName ||
    payment
        ?.supplierSnapshot
        ?.companyName ||
    payment
        ?.supplier
        ?.supplierName ||
    payment
        ?.supplier
        ?.name ||
    "Supplier";


const getSupplierMobile = (
    payment
) =>
    payment
        ?.supplierSnapshot
        ?.mobile ||
    payment
        ?.supplier
        ?.mobile ||
    "-";


const getSupplierEmail = (
    payment
) =>
    payment
        ?.supplierSnapshot
        ?.email ||
    payment
        ?.supplier
        ?.email ||
    "-";


const getErrorMessage = (
    error,
    fallback
) => {
    if (
        typeof error ===
        "string"
    ) {
        return error;
    }

    return (
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        fallback
    );
};


/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const PaymentOutDetailsPage =
    () => {
        const printRef =
            useRef(null);

        const dispatch =
            useDispatch();

        const history =
            useHistory();

        const {
            id,
        } =
            useParams();


        /*
        |--------------------------------------------------------------------------
        | REDUX
        |--------------------------------------------------------------------------
        */

        const payment =
            useSelector(
                selectedPaymentOutSelector
            );

        const loading =
            useSelector(
                paymentOutLoadingSelector
            ) || {};

        const errors =
            useSelector(
                paymentOutErrorSelector
            ) || {};


        /*
        |--------------------------------------------------------------------------
        | LOCAL STATE
        |--------------------------------------------------------------------------
        */

        const [
            reverseDialogOpen,
            setReverseDialogOpen,
        ] = useState(
            false
        );

        const [
            actionError,
            setActionError,
        ] = useState("");

        const [
            actionSuccess,
            setActionSuccess,
        ] = useState("");


        /*
        |--------------------------------------------------------------------------
        | LOAD PAYMENT
        |--------------------------------------------------------------------------
        */

        const loadPayment =
            useCallback(
                async () => {
                    if (!id) {
                        return;
                    }

                    try {
                        await dispatch(
                            fetchPaymentOutById(
                                id
                            )
                        ).unwrap();
                    } catch (
                        error
                    ) {
                        // Redux already stores details error.
                    }
                },
                [
                    dispatch,
                    id,
                ]
            );


        useEffect(
            () => {
                loadPayment();

                return () => {
                    dispatch(
                        clearSelectedPaymentOut()
                    );
                };
            },
            [
                loadPayment,
                dispatch,
            ]
        );


        /*
        |--------------------------------------------------------------------------
        | ALLOCATIONS
        |--------------------------------------------------------------------------
        */

        const allocations =
            useMemo(
                () =>
                    Array.isArray(
                        payment
                            ?.allocations
                    )
                        ? payment
                              .allocations
                        : [],
                [
                    payment,
                ]
            );


        /*
        |--------------------------------------------------------------------------
        | TOTALS
        |--------------------------------------------------------------------------
        */

        const amount =
            roundMoney(
                payment?.amount
            );

        const allocatedAmount =
            roundMoney(
                payment
                    ?.allocatedAmount
            );

        const unallocatedAmount =
            roundMoney(
                payment
                    ?.unallocatedAmount
            );


        /*
        |--------------------------------------------------------------------------
        | STATUS
        |--------------------------------------------------------------------------
        */

        const paymentStatus =
            String(
                payment?.status ||
                    ""
            ).toUpperCase();

        const isReversed =
            paymentStatus ===
                "REVERSED" ||
            payment?.isReversed ===
                true;

        const isCancelled =
            paymentStatus ===
            "CANCELLED";

        /*
         * Your Payment Out records currently use COMPLETED
         * as their successful payment status.
         *
         * Keep CONFIRMED / POSTED for compatibility if backend
         * status changes later.
         */

        const canReversePayment =
            !isReversed &&
            !isCancelled &&
            [
                "COMPLETED",
                "CONFIRMED",
                "POSTED",
            ].includes(
                paymentStatus
            );


        /*
        |--------------------------------------------------------------------------
        | REVERSE PAYMENT
        |--------------------------------------------------------------------------
        */

        const handleReversePayment =
            async (
                reversalReason
            ) => {
                if (
                    !payment?._id
                ) {
                    throw new Error(
                        "Payment Out ID is missing."
                    );
                }

                const normalizedReason =
                    String(
                        reversalReason ||
                        ""
                    ).trim();

                if (
                    !normalizedReason
                ) {
                    throw new Error(
                        "Reversal reason is required."
                    );
                }

                try {
                    setActionError(
                        ""
                    );

                    setActionSuccess(
                        ""
                    );

                    await dispatch(
                        reversePaymentOut({
                            paymentOutId:
                                payment._id,

                            reversalReason:
                                normalizedReason,

                            reason:
                                normalizedReason,
                        })
                    ).unwrap();

                    /*
                    |--------------------------------------------------------------------------
                    | CLOSE DIALOG
                    |--------------------------------------------------------------------------
                    */

                    setReverseDialogOpen(
                        false
                    );


                    /*
                    |--------------------------------------------------------------------------
                    | RELOAD PAYMENT
                    |--------------------------------------------------------------------------
                    |
                    | Important:
                    | reload from backend instead of manually changing Redux values.
                    |--------------------------------------------------------------------------
                    */

                    await dispatch(
                        fetchPaymentOutById(
                            payment._id
                        )
                    ).unwrap();

                    setActionSuccess(
                        "Supplier payment reversed successfully."
                    );
                } catch (
                    error
                ) {
                    const message =
                        getErrorMessage(
                            error,
                            "Unable to reverse supplier payment."
                        );

                    setActionError(
                        message
                    );

                    throw new Error(
                        message
                    );
                }
            };


        /*
        |--------------------------------------------------------------------------
        | PRINT PAYMENT OUT
        |--------------------------------------------------------------------------
        |
        | Native same-page printing for the actual Payment Out details page.
        |
        | - no iframe
        | - no popup/new tab
        | - neutralizes Admin Console parent layout before print
        | - waits for fonts/images
        | - restores all touched inline styles after print
        |--------------------------------------------------------------------------
        */

        const handlePrint =
            useCallback(
                async () => {
                    const printableNode =
                        printRef.current;

                    if (!printableNode) {
                        return false;
                    }

                    const touchedElements =
                        [];

                    let current =
                        printableNode;

                    while (
                        current &&
                        current !==
                        document.body
                    ) {
                        touchedElements.push({
                            element:
                                current,

                            cssText:
                                current.getAttribute(
                                    "style"
                                ),
                        });

                        current =
                            current.parentElement;
                    }

                    const forcePrintable =
                        (
                            element
                        ) => {
                            if (!element) {
                                return;
                            }

                            element.style.setProperty(
                                "display",
                                "block",
                                "important"
                            );

                            element.style.setProperty(
                                "visibility",
                                "visible",
                                "important"
                            );

                            element.style.setProperty(
                                "opacity",
                                "1",
                                "important"
                            );

                            element.style.setProperty(
                                "height",
                                "auto",
                                "important"
                            );

                            element.style.setProperty(
                                "min-height",
                                "0",
                                "important"
                            );

                            element.style.setProperty(
                                "max-height",
                                "none",
                                "important"
                            );

                            element.style.setProperty(
                                "overflow",
                                "visible",
                                "important"
                            );

                            element.style.setProperty(
                                "transform",
                                "none",
                                "important"
                            );
                        };

                    touchedElements.forEach(
                        ({
                            element,
                        }) => {
                            forcePrintable(
                                element
                            );
                        }
                    );

                    document.body.classList.add(
                        "ckw-payment-out-printing"
                    );

                    let cleanedUp =
                        false;

                    const cleanup =
                        () => {
                            if (cleanedUp) {
                                return;
                            }

                            cleanedUp =
                                true;

                            window.removeEventListener(
                                "afterprint",
                                cleanup
                            );

                            document.body.classList.remove(
                                "ckw-payment-out-printing"
                            );

                            touchedElements.forEach(
                                ({
                                    element,
                                    cssText,
                                }) => {
                                    if (
                                        cssText ===
                                        null
                                    ) {
                                        element.removeAttribute(
                                            "style"
                                        );
                                    } else {
                                        element.setAttribute(
                                            "style",
                                            cssText
                                        );
                                    }
                                }
                            );
                        };

                    window.addEventListener(
                        "afterprint",
                        cleanup,
                        {
                            once:
                                true,
                        }
                    );

                    try {
                        if (
                            document.fonts?.ready
                        ) {
                            await document
                                .fonts
                                .ready;
                        }

                        const images =
                            Array.from(
                                printableNode
                                    .querySelectorAll(
                                        "img"
                                    )
                            );

                        await Promise.all(
                            images.map(
                                (
                                    image
                                ) => {
                                    if (
                                        image.complete
                                    ) {
                                        return Promise.resolve();
                                    }

                                    return new Promise(
                                        (
                                            resolve
                                        ) => {
                                            const done =
                                                () => {
                                                    image.removeEventListener(
                                                        "load",
                                                        done
                                                    );

                                                    image.removeEventListener(
                                                        "error",
                                                        done
                                                    );

                                                    resolve();
                                                };

                                            image.addEventListener(
                                                "load",
                                                done
                                            );

                                            image.addEventListener(
                                                "error",
                                                done
                                            );
                                        }
                                    );
                                }
                            )
                        );

                        void printableNode.offsetHeight;

                        await new Promise(
                            (
                                resolve
                            ) =>
                                window.requestAnimationFrame(
                                    () =>
                                        window.requestAnimationFrame(
                                            resolve
                                        )
                                )
                        );

                        window.focus();
                        window.print();

                        window.setTimeout(
                            cleanup,
                            60000
                        );

                        return true;
                    } catch (
                        error
                    ) {
                        cleanup();

                        return false;
                    }
                },
                []
            );


        /*
        |--------------------------------------------------------------------------
        | LOADING
        |--------------------------------------------------------------------------
        */

        if (
            loading.details &&
            !payment
        ) {
            return (
                <div className="payment-out-page">
                    <div className="payment-out-empty">
                        <FiLoader className="payment-out-spinner" />

                        <h3>
                            Loading payment...
                        </h3>
                    </div>
                </div>
            );
        }


        /*
        |--------------------------------------------------------------------------
        | NOT FOUND
        |--------------------------------------------------------------------------
        */

        if (
            !loading.details &&
            !payment &&
            errors.details
        ) {
            return (
                <div className="payment-out-page">
                    <div className="payment-out-empty">
                        <FiAlertTriangle />

                        <h3>
                            Unable to load payment
                        </h3>

                        <p>
                            {errors.details}
                        </p>

                        <button
                            type="button"
                            className="payment-out-button"
                            onClick={() =>
                                history.push(
                                    "/billing/payment-out"
                                )
                            }
                        >
                            <FiArrowLeft />

                            Back to Payments
                        </button>
                    </div>
                </div>
            );
        }


        /*
        |--------------------------------------------------------------------------
        | RENDER
        |--------------------------------------------------------------------------
        */

        return (
            <div
                ref={printRef}
                className="payment-out-page"
            >

                {/* =========================================================
                    HEADER
                ========================================================= */}

                <header className="payment-out-header">
                    <div className="payment-out-heading">

                        <button
                            type="button"
                            className="payment-out-icon-button payment-out-no-print"
                            onClick={() =>
                                history.push(
                                    "/billing/payment-out"
                                )
                            }
                        >
                            <FiArrowLeft />
                        </button>

                        <span className="payment-out-heading-icon">
                            <FiCreditCard />
                        </span>

                        <div>
                            <small>
                                SUPPLIER PAYMENT
                            </small>

                            <h1>
                                {payment
                                    ?.paymentNumber ||
                                    "Payment Details"}
                            </h1>

                            <p>
                                Payment made to{" "}

                                <strong>
                                    {getSupplierName(
                                        payment
                                    )}
                                </strong>
                            </p>
                        </div>
                    </div>


                    <div className="payment-out-header-actions payment-out-no-print">

                        <button
                            type="button"
                            className="payment-out-button"
                            disabled={
                                loading.details
                            }
                            onClick={
                                loadPayment
                            }
                        >
                            <FiRefreshCw
                                className={
                                    loading.details
                                        ? "payment-out-spinner"
                                        : ""
                                }
                            />

                            Refresh
                        </button>


                        <button
                            type="button"
                            className="payment-out-button"
                            onClick={
                                handlePrint
                            }
                        >
                            <FiPrinter />

                            Print
                        </button>


                        {canReversePayment && (
                            <button
                                type="button"
                                className="payment-out-button payment-out-button-danger"
                                disabled={
                                    loading.reverse
                                }
                                onClick={() => {
                                    setActionError(
                                        ""
                                    );

                                    setActionSuccess(
                                        ""
                                    );

                                    setReverseDialogOpen(
                                        true
                                    );
                                }}
                            >
                                <FiRotateCcw />

                                Reverse Payment
                            </button>
                        )}
                    </div>
                </header>


                {/* =========================================================
                    ERRORS
                ========================================================= */}

                {errors.details && (
                    <div className="payment-out-alert">
                        {errors.details}
                    </div>
                )}


                {actionError && (
                    <div className="payment-out-action-message payment-out-action-error">
                        <FiAlertTriangle />

                        <span>
                            {actionError}
                        </span>
                    </div>
                )}


                {actionSuccess && (
                    <div className="payment-out-action-message payment-out-action-success">
                        <FiCheckCircle />

                        <span>
                            {actionSuccess}
                        </span>
                    </div>
                )}


                {/* =========================================================
                    REVERSED NOTICE
                ========================================================= */}

                {isReversed && (
                    <div className="payment-out-reversed-alert">
                        <span className="payment-out-reversed-alert-icon">
                            <FiRotateCcw />
                        </span>

                        <div>
                            <strong>
                                Payment Reversed
                            </strong>

                            <p>
                                This supplier payment has been reversed.
                                Its active purchase bill allocations and
                                supplier ledger impact have been reversed.
                            </p>

                            {payment
                                ?.reversalReason && (
                                <small>
                                    Reason:{" "}

                                    <strong>
                                        {
                                            payment
                                                .reversalReason
                                        }
                                    </strong>
                                </small>
                            )}

                            {payment
                                ?.reversedAt && (
                                <small>
                                    Reversed on{" "}

                                    {formatDate(
                                        payment
                                            .reversedAt,
                                        true
                                    )}
                                </small>
                            )}
                        </div>
                    </div>
                )}


                {/* =========================================================
                    SUMMARY
                ========================================================= */}

                <section className="payment-out-summary-grid">

                    <div className="payment-out-summary-card">
                        <span>
                            Payment Amount
                        </span>

                        <strong>
                            {formatCurrency(
                                amount
                            )}
                        </strong>

                        <small>
                            Total payment
                        </small>
                    </div>


                    <div className="payment-out-summary-card payment-out-summary-allocated">
                        <span>
                            Allocated
                        </span>

                        <strong>
                            {formatCurrency(
                                allocatedAmount
                            )}
                        </strong>

                        <small>
                            Against purchase bills
                        </small>
                    </div>


                    <div className="payment-out-summary-card payment-out-summary-advance">
                        <span>
                            Supplier Advance
                        </span>

                        <strong>
                            {formatCurrency(
                                unallocatedAmount
                            )}
                        </strong>

                        <small>
                            Unallocated amount
                        </small>
                    </div>


                    <div className="payment-out-summary-card payment-out-summary-total">
                        <span>
                            Status
                        </span>

                        <strong>
                            {formatLabel(
                                payment
                                    ?.status ||
                                    "COMPLETED"
                            )}
                        </strong>

                        <small>
                            Payment status
                        </small>
                    </div>
                </section>


                {/* =========================================================
                    SUPPLIER DETAILS
                ========================================================= */}

                <section className="payment-out-view-card">

                    <div className="payment-out-view-card-title">
                        <FiUser />

                        <div>
                            <h3>
                                Supplier Details
                            </h3>

                            <p>
                                Supplier information captured with this payment.
                            </p>
                        </div>
                    </div>


                    <div className="payment-out-view-info-grid">

                        <div>
                            <span>
                                Supplier
                            </span>

                            <strong>
                                {getSupplierName(
                                    payment
                                )}
                            </strong>
                        </div>


                        <div>
                            <span>
                                Mobile
                            </span>

                            <strong>
                                {getSupplierMobile(
                                    payment
                                )}
                            </strong>
                        </div>


                        <div>
                            <span>
                                Email
                            </span>

                            <strong>
                                {getSupplierEmail(
                                    payment
                                )}
                            </strong>
                        </div>


                        <div>
                            <span>
                                Company
                            </span>

                            <strong>
                                {payment
                                    ?.supplierSnapshot
                                    ?.companyName ||
                                    "-"}
                            </strong>
                        </div>


                        <div>
                            <span>
                                GSTIN
                            </span>

                            <strong>
                                {payment
                                    ?.supplierSnapshot
                                    ?.gstin ||
                                    "-"}
                            </strong>
                        </div>


                        <div>
                            <span>
                                PAN
                            </span>

                            <strong>
                                {payment
                                    ?.supplierSnapshot
                                    ?.pan ||
                                    "-"}
                            </strong>
                        </div>

                    </div>
                </section>


                {/* =========================================================
                    PAYMENT INFORMATION
                ========================================================= */}

                <section className="payment-out-view-card">

                    <div className="payment-out-view-card-title">
                        <FiCreditCard />

                        <div>
                            <h3>
                                Payment Information
                            </h3>

                            <p>
                                Payment method and transaction details.
                            </p>
                        </div>
                    </div>


                    <div className="payment-out-view-info-grid">

                        <div>
                            <span>
                                Payment Number
                            </span>

                            <strong>
                                {payment
                                    ?.paymentNumber ||
                                    "-"}
                            </strong>
                        </div>


                        <div>
                            <span>
                                Payment Date
                            </span>

                            <strong>
                                {formatDate(
                                    payment
                                        ?.paymentDate
                                )}
                            </strong>
                        </div>


                        <div>
                            <span>
                                Financial Year
                            </span>

                            <strong>
                                {payment
                                    ?.financialYear ||
                                    "-"}
                            </strong>
                        </div>


                        <div>
                            <span>
                                Payment Method
                            </span>

                            <strong>
                                {formatLabel(
                                    payment
                                        ?.paymentMethod
                                )}
                            </strong>
                        </div>


                        <div>
                            <span>
                                Reference / UTR
                            </span>

                            <strong>
                                {payment
                                    ?.referenceNumber ||
                                    "-"}
                            </strong>
                        </div>


                        <div>
                            <span>
                                Bank
                            </span>

                            <strong>
                                {payment
                                    ?.bankName ||
                                    "-"}
                            </strong>
                        </div>


                        {payment
                            ?.paymentMethod ===
                            "CHEQUE" && (
                            <>
                                <div>
                                    <span>
                                        Cheque Number
                                    </span>

                                    <strong>
                                        {payment
                                            ?.chequeNumber ||
                                            "-"}
                                    </strong>
                                </div>

                                <div>
                                    <span>
                                        Cheque Date
                                    </span>

                                    <strong>
                                        {formatDate(
                                            payment
                                                ?.chequeDate
                                        )}
                                    </strong>
                                </div>
                            </>
                        )}


                        <div>
                            <span>
                                Posted At
                            </span>

                            <strong>
                                {formatDate(
                                    payment
                                        ?.postingDate ||
                                        payment
                                            ?.createdAt,
                                    true
                                )}
                            </strong>
                        </div>

                    </div>
                </section>


                {/* =========================================================
                    PURCHASE BILL ALLOCATIONS
                ========================================================= */}

                <section className="payment-out-view-card">

                    <div className="payment-out-view-card-title">
                        <FiFileText />

                        <div>
                            <h3>
                                Purchase Bill Allocations
                            </h3>

                            <p>
                                Payment amount allocated against supplier purchase bills.
                            </p>
                        </div>
                    </div>


                    {allocations.length ===
                    0 ? (
                        <div className="payment-out-view-empty">
                            <strong>
                                No purchase bill allocation
                            </strong>

                            <span>
                                {isReversed
                                    ? "The payment allocations have been reversed."
                                    : "The full payment was recorded as supplier advance."}
                            </span>
                        </div>
                    ) : (
                        <div className="payment-out-allocation-table-wrapper">

                            <table className="payment-out-allocation-table">

                                <thead>
                                    <tr>
                                        <th>
                                            Purchase Bill
                                        </th>

                                        <th>
                                            Bill Date
                                        </th>

                                        <th>
                                            Due Date
                                        </th>

                                        <th className="text-right">
                                            Bill Amount
                                        </th>

                                        <th className="text-right">
                                            Balance Before
                                        </th>

                                        <th className="text-right">
                                            Paid
                                        </th>

                                        <th className="text-right">
                                            Balance After
                                        </th>

                                        <th>
                                            Status
                                        </th>
                                    </tr>
                                </thead>


                                <tbody>
                                    {allocations.map(
                                        (
                                            allocation,
                                            index
                                        ) => (
                                            <tr
                                                key={
                                                    allocation
                                                        ?._id ||
                                                    index
                                                }
                                            >
                                                <td>
                                                    <strong className="payment-out-bill-number">
                                                        {allocation
                                                            ?.purchaseBillNumber ||
                                                            "-"}
                                                    </strong>
                                                </td>


                                                <td>
                                                    {formatDate(
                                                        allocation
                                                            ?.purchaseBillDate
                                                    )}
                                                </td>


                                                <td>
                                                    {formatDate(
                                                        allocation
                                                            ?.dueDate
                                                    )}
                                                </td>


                                                <td className="text-right">
                                                    {formatCurrency(
                                                        allocation
                                                            ?.billAmount
                                                    )}
                                                </td>


                                                <td className="text-right">
                                                    {formatCurrency(
                                                        allocation
                                                            ?.balanceBeforePayment
                                                    )}
                                                </td>


                                                <td className="text-right payment-out-amount">
                                                    {formatCurrency(
                                                        allocation
                                                            ?.allocatedAmount
                                                    )}
                                                </td>


                                                <td className="text-right">
                                                    {formatCurrency(
                                                        allocation
                                                            ?.balanceAfterPayment
                                                    )}
                                                </td>


                                                <td>
                                                    <span
                                                        className={`payment-out-status payment-out-status-${String(
                                                            allocation
                                                                ?.status ||
                                                                "ACTIVE"
                                                        ).toLowerCase()}`}
                                                    >
                                                        {formatLabel(
                                                            allocation
                                                                ?.status ||
                                                                "ACTIVE"
                                                        )}
                                                    </span>
                                                </td>
                                            </tr>
                                        )
                                    )}
                                </tbody>

                            </table>

                        </div>
                    )}
                </section>


                {/* =========================================================
                    TOTALS
                ========================================================= */}

                <section className="payment-out-view-card">

                    <div className="payment-out-total-box">

                        <div>
                            <span>
                                Payment Amount
                            </span>

                            <strong>
                                {formatCurrency(
                                    amount
                                )}
                            </strong>
                        </div>


                        <div>
                            <span>
                                Allocated
                            </span>

                            <strong>
                                {formatCurrency(
                                    allocatedAmount
                                )}
                            </strong>
                        </div>


                        <div>
                            <span>
                                Supplier Advance
                            </span>

                            <strong>
                                {formatCurrency(
                                    unallocatedAmount
                                )}
                            </strong>
                        </div>

                    </div>
                </section>


                {/* =========================================================
                    NOTES
                ========================================================= */}

                {(payment?.narration ||
                    payment
                        ?.internalNotes) && (
                    <section className="payment-out-view-card">

                        <div className="payment-out-view-card-title">
                            <FiFileText />

                            <div>
                                <h3>
                                    Notes
                                </h3>
                            </div>
                        </div>


                        {payment
                            ?.narration && (
                            <div className="payment-out-note-box">
                                {
                                    payment
                                        .narration
                                }
                            </div>
                        )}


                        {payment
                            ?.internalNotes && (
                            <div className="payment-out-note-box">
                                <strong>
                                    Internal:
                                </strong>{" "}

                                {
                                    payment
                                        .internalNotes
                                }
                            </div>
                        )}

                    </section>
                )}


                {/* =========================================================
                    CANCELLED
                ========================================================= */}

                {isCancelled && (
                    <section className="payment-out-view-card">

                        <div className="payment-out-alert">

                            <div>
                                <strong>
                                    Payment Cancelled
                                </strong>

                                <div>
                                    {payment
                                        ?.cancellationReason ||
                                        "-"}
                                </div>

                                <small>
                                    {formatDate(
                                        payment
                                            ?.cancelledAt,
                                        true
                                    )}
                                </small>
                            </div>

                        </div>

                    </section>
                )}


                {/* =========================================================
                    REVERSE PAYMENT DIALOG
                ========================================================= */}

                <ReversePaymentOutDialog
                    open={
                        reverseDialogOpen
                    }
                    paymentOut={
                        payment
                    }
                    loading={
                        Boolean(
                            loading.reverse
                        )
                    }
                    onClose={() =>
                        setReverseDialogOpen(
                            false
                        )
                    }
                    onConfirm={
                        handleReversePayment
                    }
                />

            </div>
        );
    };


export default PaymentOutDetailsPage;